import {
  sendUserFeedback,
  setError,
} from "@/redux/slices/commonSlices/requestSlice";

import React, {useEffect, useState} from "react";

import logoVioletEn from "@images/general/ava_trainer_violet_logo_EN.png";
import closeImg from "@images/popups/close.svg";
import {usePopup} from "@layouts/popup/InfoPopup/PopupContext";
import {AiFeedbackPopup} from "@popups/simulationPopups/AiFeedbackPopup";
import {RootButton} from "@ui/buttons/RootButton";
import {RootDesc} from "@ui/descriptions/RootDesc";
import {SmallDesc} from "@ui/descriptions/SmallDesc";
import {Spinner} from "@ui/general/Spinner";
import {RadioGroup} from "@ui/inputs/RootRadio";
import moment from "moment";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {useLocation} from "react-router-dom";

import styles from "./styles.module.scss";

export const LeaveFeedbackPopup = ({handleTryAgain, handleQuit}) => {
  const location = useLocation();
  const dispatch = useDispatch();

  const {openPopup, closePopup} = usePopup();

  const [activeStep, setActiveStep] = useState(1);
  const [data, setData] = useState([]);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [otherValues, setOtherValues] = useState({});
  const [errors, setErrors] = useState({});

  const userName = useSelector((state) => state.common.candidate.name);
  const userLastName = useSelector((state) => state.common.candidate.lastname);
  const {start_time} = useSelector((state) => state.ml.chat);
  const {isMobile} = useSelector((state) => state.common.settings);

  const {t} = useTranslation();

  const stepsData = [
    {
      id: 0,
      count: 1,
      name: "Step 1",
      required: true,
      title: "Test information",
      body: (
        <>
          <RadioGroup
            required
            title={"What did you test today?"}
            name={"type"}
            value={selectedOptions["type"] || ""}
            onChange={(option) => handleRadioChange("type", option)}
            layout={"vertical"}
            otherValue={otherValues["type"] || ""}
            onOtherChange={(value) => handleOtherChange("type", value)}
            options={[
              {
                id: "type-1",
                value: "call_simulation",
                label: "Call simulation",
              },
              {
                id: "type-2",
                value: "product_bootcamp",
                label: "Product Bootcamp",
              },
              {
                id: "type-3",
                value: "other",
                label: "Other",
              },
            ]}
          />

          <RadioGroup
            input
            required
            title={"Which specific scenario/product did you test?"}
            name={"simulation-name"}
            value={selectedOptions["simulation-name"] || ""}
            onChange={(option) => handleRadioChange("simulation-name", option)}
            layout={"vertical"}
            otherValue={otherValues["simulation-name"] || ""}
            onOtherChange={(value) =>
              handleOtherChange("simulation-name", value)
            }
            options={[
              {
                id: "simulation-name",
                value: "other",
                label: "Enter your answer",
              },
            ]}
          />

          <RadioGroup
            required
            title={"Which difficulty level did you select?"}
            name={"level"}
            value={selectedOptions["level"] || ""}
            onChange={(option) => handleRadioChange("level", option)}
            layout={"vertical"}
            options={[
              {
                id: "level-1",
                value: "easy",
                label: "Easy",
              },
              {
                id: "level-2",
                value: "medium",
                label: "Medium",
              },
              {
                id: "level-3",
                value: "hard",
                label: "Hard",
              },
            ]}
          />

          <RadioGroup
            input
            required
            title={"What was the approximate date and time of your test?"}
            name={"time"}
            value={selectedOptions["time"] || ""}
            onChange={(option) => handleRadioChange("time", option)}
            layout={"vertical"}
            otherValue={otherValues["time"] || ""}
            onOtherChange={(value) => handleOtherChange("time", value)}
            options={[
              {
                id: "time",
                value: "other",
                label: "Enter your answer",
              },
            ]}
          />

          <RadioGroup
            input
            required
            title={"Which account did you use to log in?"}
            name={"account-name"}
            value={selectedOptions["account-name"] || ""}
            onChange={(option) => handleRadioChange("account-name", option)}
            layout={"vertical"}
            otherValue={otherValues["account-name"] || ""}
            onOtherChange={(value) => handleOtherChange("account-name", value)}
            options={[
              {
                id: "account-name",
                value: "other",
                label: "Enter your answer",
              },
            ]}
          />
        </>
      ),
    },
    {
      id: 1,
      count: 2,
      name: "Step 2",
      required: true,
      title: "Identifying specific issues",
      body: (
        <>
          <RadioGroup
            required
            title={"Did you encounter any bugs or errors during your test?"}
            name={"bugs"}
            value={selectedOptions["bugs"] || ""}
            onChange={(option) => handleRadioChange("bugs", option)}
            layout={"vertical"}
            otherValue={otherValues["bugs"] || ""}
            onOtherChange={(value) => handleOtherChange("bugs", value)}
            options={[
              {
                id: "bugs-1",
                value: "no",
                label: "No",
              },
              {
                id: "bugs-2",
                value: "other",
                label: "Other",
              },
            ]}
          />

          <RadioGroup
            input
            required={false}
            title={
              "If a response or feature did not work as expected, what was the exact input/question you used, and what was Ava's output?"
            }
            name={"simulation-output"}
            value={selectedOptions["simulation-output"] || ""}
            onChange={(option) =>
              handleRadioChange("simulation-output", option)
            }
            layout={"vertical"}
            otherValue={otherValues["simulation-output"] || ""}
            onOtherChange={(value) =>
              handleOtherChange("simulation-output", value)
            }
            options={[
              {
                id: "simulation-output",
                value: "other",
                label: "Enter your answer",
              },
            ]}
          />

          <RadioGroup
            required
            title={
              "Was there any issue with transcription (speech-to-text) accuracy?"
            }
            name={"stt"}
            value={selectedOptions["stt"] || ""}
            onChange={(option) => handleRadioChange("stt", option)}
            layout={"vertical"}
            options={[
              {
                id: "stt-1",
                value: "no",
                label: "No",
              },
              {
                id: "stt-2",
                value: "yes",
                label: "Yes",
              },
            ]}
          />

          {selectedOptions["stt"] === "yes" && (
            <RadioGroup
              input
              required={false}
              title={
                "If you encountered Speech-transcription (speech-to-text) inaccuracies, please provide more information here"
              }
              name={"stt-output-more"}
              value={selectedOptions["stt-output-more"] || ""}
              onChange={(option) =>
                handleRadioChange("stt-output-more", option)
              }
              layout={"vertical"}
              otherValue={otherValues["stt-output-more"] || ""}
              onOtherChange={(value) =>
                handleOtherChange("stt-output-more", value)
              }
              options={[
                {
                  id: "stt-output-more",
                  value: "other",
                  label: "Enter your answer",
                },
              ]}
            />
          )}

          <RadioGroup
            required={false}
            title={
              "Did Ava's avatar's lip-syncing align with the speech output?"
            }
            name={"stt-output"}
            value={selectedOptions["stt-output"] || ""}
            onChange={(option) => handleRadioChange("stt-output", option)}
            layout={"vertical"}
            options={[
              {
                id: "stt-output-1",
                value: "consistently",
                label: "Yes, consistently",
              },
              {
                id: "stt-output-2",
                value: "aligned",
                label: "Mostly aligned",
              },
              {
                id: "stt-output-3",
                value: "misaligned",
                label: "Somewhat misaligned",
              },
              {
                id: "stt-output-4",
                value: "not_aligned",
                label: "Not aligned at all",
              },
            ]}
          />
        </>
      ),
    },
    {
      id: 2,
      count: 3,
      name: "Step 3",
      required: false,
      title: "Realism and Context",
      body: (
        <>
          <RadioGroup
            input
            required={false}
            title={
              "How realistic were Ava's responses to your specific inputs/questions?"
            }
            name={"realistic"}
            value={selectedOptions["realistic"] || ""}
            onChange={(option) => handleRadioChange("realistic", option)}
            layout={"vertical"}
            otherValue={otherValues["realistic"] || ""}
            onOtherChange={(value) => handleOtherChange("realistic", value)}
            options={[
              {
                id: "realistic",
                value: "other",
                label:
                  "Please share specific sentences or interactions that were realistic or unrealistic.",
              },
            ]}
          />

          <RadioGroup
            rating
            required={false}
            title={
              "Were Ava's responses contextually relevant to your professional experience?"
            }
            name={"context"}
            value={selectedOptions["context"] || ""}
            onChange={(option) => handleRadioChange("context", option)}
            layout={"vertical"}
            minLabel={"Not relevant"}
            maxLabel={"Very relevant"}
          />
        </>
      ),
    },
    {
      id: 3,
      count: 4,
      name: "Step 4",
      required: true,
      title: "Usability and Navigation",
      body: (
        <>
          <RadioGroup
            rating
            required={false}
            title={"How easy was the interface to navigate and use?"}
            name={"navigate"}
            value={selectedOptions["navigate"] || ""}
            onChange={(option) => handleRadioChange("navigate", option)}
            layout={"vertical"}
            minLabel={"Difficult"}
            maxLabel={"Very easy"}
          />

          <RadioGroup
            required
            title={
              "Did you encounter any issues while accessing or switching between scenarios?"
            }
            name={"switching"}
            value={selectedOptions["switching"] || ""}
            onChange={(option) => handleRadioChange("switching", option)}
            layout={"vertical"}
            otherValue={otherValues["switching"] || ""}
            onOtherChange={(value) => handleOtherChange("switching", value)}
            options={[
              {
                id: "switching-1",
                value: "no",
                label: "No",
              },
              {
                id: "switching-2",
                value: "other",
                label: "Other",
              },
            ]}
          />
        </>
      ),
    },
    {
      id: 4,
      count: 5,
      name: "Step 5",
      required: false,
      title: "Improvements and Suggestions",
      body: (
        <>
          <RadioGroup
            input
            required={false}
            title={
              "What additional scenarios or features would you suggest to improve Ava's training simulations?"
            }
            name={"additional-scenarios"}
            value={selectedOptions["additional-scenarios"] || ""}
            onChange={(option) =>
              handleRadioChange("additional-scenarios", option)
            }
            layout={"vertical"}
            otherValue={otherValues["additional-scenarios"] || ""}
            onOtherChange={(value) =>
              handleOtherChange("additional-scenarios", value)
            }
            options={[
              {
                id: "additional-scenarios",
                value: "other",
                label: "Enter your answer",
              },
            ]}
          />

          <RadioGroup
            input
            required={false}
            title={
              "Could you suggest additional doctor profiles or traits to include?"
            }
            name={"additional-doctor"}
            value={selectedOptions["additional-doctor"] || ""}
            onChange={(option) =>
              handleRadioChange("additional-doctor", option)
            }
            layout={"vertical"}
            otherValue={otherValues["additional-doctor"] || ""}
            onOtherChange={(value) =>
              handleOtherChange("additional-doctor", value)
            }
            options={[
              {
                id: "additional-doctor",
                value: "other",
                label:
                  "Include details such as specialty, years of experience, patient count, or communication style.",
              },
            ]}
          />

          <RadioGroup
            input
            required={false}
            title={"What do you think is Ava's greatest strength?"}
            name={"strength"}
            value={selectedOptions["strength"] || ""}
            onChange={(option) => handleRadioChange("strength", option)}
            layout={"vertical"}
            otherValue={otherValues["strength"] || ""}
            onOtherChange={(value) => handleOtherChange("strength", value)}
            options={[
              {
                id: "strength",
                value: "other",
                label: "Enter your answer",
              },
            ]}
          />

          <RadioGroup
            input
            required={false}
            title={"What do you think is Ava's most significant limitation?"}
            name={"limitation"}
            value={selectedOptions["limitation"] || ""}
            onChange={(option) => handleRadioChange("limitation", option)}
            layout={"vertical"}
            otherValue={otherValues["limitation"] || ""}
            onOtherChange={(value) => handleOtherChange("limitation", value)}
            options={[
              {
                id: "limitation",
                value: "other",
                label: "Enter your answer",
              },
            ]}
          />

          <RadioGroup
            input
            required={false}
            title={
              "Do you have any other feedback or suggestions to improve Ava?"
            }
            name={"suggestions"}
            value={selectedOptions["suggestions"] || ""}
            onChange={(option) => handleRadioChange("suggestions", option)}
            layout={"vertical"}
            otherValue={otherValues["suggestions"] || ""}
            onOtherChange={(value) => handleOtherChange("suggestions", value)}
            options={[
              {
                id: "suggestions",
                value: "other",
                label: "Enter your answer",
              },
            ]}
          />
        </>
      ),
    },
  ];

  useEffect(() => {
    const initialData = stepsData.map((step) => ({
      stepId: step.id,
      step: step.name,
      answers: [],
    }));

    setData(initialData);
  }, []);

  useEffect(() => {
    if (data.length === 0) return;

    if (location.state && location.state.desc) {
      const {type, level, name} = location.state.desc;

      if (type) {
        const simulationTypeOption = {
          id: `type-${type === "scenario" ? "1" : "2"}`,
          value: type === "scenario" ? "call_simulation" : "product_bootcamp",
          label: type === "scenario" ? "Call simulation" : "Product Bootcamp",
        };

        handleRadioChange("type", simulationTypeOption);
      }

      if (level) {
        const simulationLevelOption = {
          id: `level-${
            level === "easy" ? "1" : level === "medium" ? "2" : "3"
          }`,
          value: level,
          label: level.charAt(0).toUpperCase() + level.slice(1),
        };

        handleRadioChange("level", simulationLevelOption);
      }

      if (name) {
        const simulationNameOption = {
          id: `simulation-name`,
          value: name || "",
          label: name,
        };

        handleRadioChange("simulation-name", simulationNameOption);
        handleOtherChange("simulation-name", name);
      }
    }

    if (userName) {
      const userNameOption = {
        id: `account-name`,
        value: `${userLastName || ""} ${userName || ""}`,
        label: userName,
      };

      handleRadioChange("account-name", userNameOption);
      handleOtherChange(
        "account-name",
        `${userLastName || ""} ${userName || ""}`,
      );
    }

    if (start_time) {
      const currentTime = moment(start_time).format("DD-MM-YYYY HH:mm:ss");
      const timeOption = {
        id: `time`,
        value: currentTime,
        label: currentTime,
      };

      handleRadioChange("time", timeOption);
      handleOtherChange("time", currentTime);
    }
  }, [location.state, start_time, userName, userLastName, data.length]);

  const validateStep = (step) => {
    const currentStep = stepsData[step - 1];
    if (!currentStep.required) return true;
    if (!currentStep.body || typeof currentStep.body === "string") return true;

    const {children} = currentStep.body.props;
    if (!children) return true;

    const radioGroups = Array.isArray(children) ? children : [children];

    return radioGroups.every((group) => {
      if (!group?.props?.required) return true;

      const {name} = group.props;
      const value = selectedOptions[name];

      if (value === "other") {
        return otherValues[name] && otherValues[name].trim() !== "";
      }

      return value !== undefined && value !== "";
    });
  };

  const canProceedToNextStep = () => {
    return validateStep(activeStep);
  };

  const canNavigateToStep = (step) => {
    if (step < activeStep) return true;

    for (let i = activeStep; i <= step; i++) {
      if (!validateStep(i)) return false;
    }

    return true;
  };

  const handleClickNext = () => {
    if (activeStep < stepsData.length && canProceedToNextStep()) {
      setActiveStep(activeStep + 1);
      setErrors({});
    } else {
      const currentStep = stepsData[activeStep - 1];
      const newErrors = {};

      if (currentStep.body && typeof currentStep.body !== "string") {
        const {children} = currentStep.body.props;
        const radioGroups = Array.isArray(children) ? children : [children];

        radioGroups.forEach((group) => {
          if (group.props.required) {
            const {name} = group.props;
            const value = selectedOptions[name];

            if (
              !value ||
              (value === "other" &&
                (!otherValues[name] || otherValues[name].trim() === ""))
            ) {
              newErrors[name] = "This field is required";
            }
          }
        });
      }

      setErrors(newErrors);
    }
  };

  const handleClickPrev = () => {
    if (activeStep > 1) {
      setActiveStep(activeStep - 1);
      setErrors({});
    }
  };

  const handleClickStep = (step) => {
    if (activeStep !== step.count && canNavigateToStep(step.count)) {
      setActiveStep(step.count);
      setErrors({});
    }
  };

  const onClickClose = () => {
    closePopup();
    openPopup(
      <AiFeedbackPopup
        again={handleTryAgain}
        quit={handleQuit}
      />,
      {
        closeButton: false,
      },
    );
  };

  const handleClickSubmit = async () => {
    if (canProceedToNextStep()) {
      const resultAction = await dispatch(sendUserFeedback({steps: data}));

      if (sendUserFeedback.fulfilled.match(resultAction)) {
        closePopup();

        openPopup(
          <AiFeedbackPopup
            again={handleTryAgain}
            quit={handleQuit}
          />,
          {
            closeButton: false,
          },
        );
      } else {
        dispatch(setError(t("server_error.login")));
      }
    } else {
      const currentStep = stepsData[activeStep - 1];
      const newErrors = {};

      if (currentStep.body && typeof currentStep.body !== "string") {
        const {children} = currentStep.body.props;
        const radioGroups = Array.isArray(children) ? children : [children];

        radioGroups.forEach((group) => {
          if (group.props.required) {
            const {name} = group.props;
            const value = selectedOptions[name];

            if (
              !value ||
              (value === "other" &&
                (!otherValues[name] || otherValues[name].trim() === ""))
            ) {
              newErrors[name] = "This field is required";
            }
          }
        });
      }

      setErrors(newErrors);
    }
  };

  const handleRadioChange = (name, option) => {
    try {
      const stepId = determineStepId(name);
      const currentStepId = stepId >= 0 ? stepId : activeStep - 1;

      let radioGroup = null;
      const step = stepsData[currentStepId];

      if (step && step.body && typeof step.body !== "string") {
        const {children} = step.body.props;
        if (children) {
          const radioGroups = Array.isArray(children) ? children : [children];

          radioGroup = radioGroups.find(
            (child) => child && child.props && child.props.name === name,
          );
        }
      }

      if (!radioGroup) {
        console.error(
          `Radio group with name "${name}" not found in step ${currentStepId}!`,
        );

        return;
      }

      const isRating = radioGroup.props.rating;
      const optionValue = isRating ? option : option.value;
      const questionTitle = radioGroup.props.title;

      setSelectedOptions((prev) => ({
        ...prev,
        [name]: optionValue,
      }));

      updateData({
        stepId: currentStepId,
        name,
        title: questionTitle,
        value: optionValue,
        label: isRating ? optionValue : option.label,
        customValue: optionValue === "other" ? otherValues[name] : null,
      });

      setErrors((prev) => {
        const newErrors = {...prev};
        delete newErrors[name];
        return newErrors;
      });
    } catch (error) {
      console.error("Error in handleRadioChange: ", error);
    }
  };

  const handleOtherChange = (name, value) => {
    try {
      setOtherValues((prev) => ({
        ...prev,
        [name]: value,
      }));

      const stepId = determineStepId(name);
      const currentStepId = stepId >= 0 ? stepId : activeStep - 1;

      let radioGroup = null;
      const step = stepsData[currentStepId];

      if (step && step.body && typeof step.body !== "string") {
        const {children} = step.body.props;
        if (children) {
          const radioGroups = Array.isArray(children) ? children : [children];

          radioGroup = radioGroups.find(
            (child) => child && child.props && child.props.name === name,
          );
        }
      }

      if (!radioGroup) {
        console.error(
          `Radio group with name "${name}" not found in step ${currentStepId}!`,
        );

        return;
      }

      const otherOption = radioGroup.props.options.find(
        (option) => option.value === "other",
      );
      const questionTitle = radioGroup.props.title;

      updateData({
        stepId: currentStepId,
        name,
        title: questionTitle,
        value: "other",
        label: otherOption ? otherOption.label : "",
        customValue: value,
      });

      if (value && value.trim() !== "") {
        setErrors((prev) => {
          const newErrors = {...prev};
          delete newErrors[name];
          return newErrors;
        });
      }
    } catch (error) {
      console.error("Error in handleOtherChange: ", error);
    }
  };

  const determineStepId = (name) => {
    for (let i = 0; i < stepsData.length; i++) {
      const step = stepsData[i];

      if (step.body && typeof step.body !== "string") {
        const {children} = step.body.props;

        if (children) {
          const radioGroups = Array.isArray(children) ? children : [children];
          const hasMatchingRadioGroup = radioGroups.some(
            (child) => child && child.props && child.props.name === name,
          );

          if (hasMatchingRadioGroup) {
            return i;
          }
        }
      }
    }

    if (
      name.startsWith("type") ||
      name.startsWith("simulation-name") ||
      name.startsWith("level") ||
      name.startsWith("time") ||
      name.startsWith("account-name")
    ) {
      return 0;
    } else if (
      name.startsWith("bugs") ||
      name.startsWith("simulation-output") ||
      name.startsWith("stt") ||
      name.startsWith("stt-output-more") ||
      name.startsWith("stt-output")
    ) {
      return 1;
    } else if (name.startsWith("realistic") || name.startsWith("context")) {
      return 2;
    } else if (name.startsWith("navigate") || name.startsWith("switching")) {
      return 3;
    } else if (
      name.startsWith("additional-scenarios") ||
      name.startsWith("additional-doctor") ||
      name.startsWith("strength") ||
      name.startsWith("limitation") ||
      name.startsWith("suggestions")
    ) {
      return 4;
    }

    return activeStep - 1;
  };

  const updateData = (item) => {
    const newData = [...data];
    const stepIndex = newData.findIndex((step) => step.stepId === item.stepId);

    if (stepIndex >= 0) {
      let radioGroup = null;
      const step = stepsData[item.stepId];

      if (step && step.body && typeof step.body !== "string") {
        const {children} = step.body.props;

        if (children) {
          const radioGroups = Array.isArray(children) ? children : [children];

          radioGroup = radioGroups.find(
            (child) => child && child.props && child.props.name === item.name,
          );
        }
      }

      const questionTitle =
        radioGroup && radioGroup.props ? radioGroup.props.title : item.title;
      const answerIndex = newData[stepIndex].answers.findIndex(
        (answer) => answer.question === questionTitle,
      );

      if (answerIndex >= 0) {
        newData[stepIndex].answers[answerIndex] = {
          question: questionTitle,
          answer: item.value === "other" ? item.customValue : item.label,
        };
      } else {
        newData[stepIndex].answers.push({
          question: questionTitle,
          answer: item.value === "other" ? item.customValue : item.label,
        });
      }

      setData(newData);
    }
  };

  const renderRadioGroup = (group) => {
    if (!group || !group.props || React.Fragment === group.type) {
      return null;
    }

    const {name} = group.props;
    const hasError = errors[name];

    return (
      <div
        key={name}
        className={hasError ? styles.errorGroup : ""}
      >
        {React.cloneElement(group, {
          ...group.props,
          className: `${group.props.className || ""} ${
            hasError ? styles.error : ""
          }`,
        })}
        {hasError && <div className={styles.errorMessage}>{errors[name]}</div>}
      </div>
    );
  };

  return (
    <div className={styles.leave_feedback_wrapper}>
      {status === "loading" && <Spinner width={isMobile ? 60 : 80} />}

      <div
        onClick={onClickClose}
        className={styles.leave_feedback_close}
      >
        <img
          src={closeImg}
          alt={"close"}
        />
      </div>

      <div className={styles.leave_feedback_logo}>
        <img
          src={logoVioletEn}
          alt={"ava-logo"}
        />
      </div>

      <h3>Tell us what you think of Ava!</h3>

      <div className={styles.leave_feedback_content}>
        {stepsData &&
          stepsData.map(
            (content) =>
              content.count === activeStep && (
                <div
                  key={content.id}
                  className={styles.step_content}
                >
                  {content.required && (
                    <RootDesc>
                      <span style={{color: "var(--red)"}}>
                        * Required fields
                      </span>
                    </RootDesc>
                  )}

                  <h2>{content.title}</h2>

                  <div className={styles.step_content_body}>
                    {content.body
                      ? Array.isArray(content.body.props.children)
                        ? content.body.props.children.map(renderRadioGroup)
                        : renderRadioGroup(content.body.props.children)
                      : ""}
                  </div>
                </div>
              ),
          )}
      </div>

      <div className={styles.leave_feedback_btns}>
        {activeStep > 1 && (
          <RootButton
            txt={"Back to Previous Step"}
            type={"button"}
            onClick={handleClickPrev}
          />
        )}

        {activeStep !== stepsData.length && (
          <RootButton
            txt={"Next Step"}
            type={"button"}
            onClick={handleClickNext}
            disabled={!canProceedToNextStep()}
          />
        )}

        {activeStep === stepsData.length && (
          <RootButton
            txt={"Submit"}
            type={"button"}
            onClick={handleClickSubmit}
            disabled={!canProceedToNextStep()}
          />
        )}
      </div>

      <ul className={styles.leave_feedback_steps}>
        {stepsData &&
          stepsData.map((step) => (
            <li
              key={step.id}
              onClick={() =>
                data.length === stepsData.length
                  ? handleClickStep(step)
                  : undefined
              }
              style={
                activeStep === step.count
                  ? {cursor: "default", pointerEvents: "none"}
                  : canNavigateToStep(step.count)
                    ? {cursor: "pointer", pointerEvents: "all"}
                    : {
                        cursor: "not-allowed",
                        pointerEvents: "none",
                        opacity: 0.5,
                      }
              }
            >
              <article
                style={{
                  border: `${
                    activeStep === step.count
                      ? "6rem solid var(--violet)"
                      : activeStep > step.count
                        ? "4rem solid var(--violetLight)"
                        : "4rem solid var(--grey)"
                  }`,
                }}
              >
                {step.id !== stepsData.length - 1 && (
                  <i
                    style={{
                      background: `${
                        activeStep - 1 >= step.count
                          ? "var(--violetLight)"
                          : "var(--greyLight)"
                      }`,
                    }}
                  />
                )}
              </article>

              <SmallDesc>
                <span
                  style={
                    activeStep === step.count
                      ? {fontWeight: "600", color: "var(--violet)"}
                      : {fontWeight: "400", color: "var(--grey)"}
                  }
                >
                  {step.name}
                </span>
              </SmallDesc>
            </li>
          ))}
      </ul>
    </div>
  );
};
